import { OUTPUT_SAMPLE_RATE } from '../../shared/config.ts';
import { base64ToInt16, int16ToFloat } from './pcm.ts';

/** Gapless playback of 24 kHz PCM chunks. `interrupt()` drops everything queued (user barged in). */
export class AudioPlayer {
  private ctx: AudioContext | null = null;
  private playhead = 0;
  private sources = new Set<AudioBufferSourceNode>();
  onIdle: (() => void) | null = null;
  level = 0; // rough output level for the orb, 0..1

  /** Must be called from a user gesture (Start tap); browsers keep audio suspended otherwise. */
  async unlock() {
    this.ctx ??= new AudioContext({ sampleRate: OUTPUT_SAMPLE_RATE });
    if (this.ctx.state === 'suspended') await this.ctx.resume();
  }

  get speaking() {
    return this.sources.size > 0;
  }

  enqueue(b64: string) {
    if (!this.ctx || !b64) return;
    const samples = int16ToFloat(base64ToInt16(b64));
    if (samples.length === 0) return;
    let peak = 0;
    for (let i = 0; i < samples.length; i += 32) peak = Math.max(peak, Math.abs(samples[i]!));
    this.level = peak;
    const buf = this.ctx.createBuffer(1, samples.length, OUTPUT_SAMPLE_RATE);
    buf.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.connect(this.ctx.destination);
    this.playhead = Math.max(this.playhead, this.ctx.currentTime + 0.02);
    src.start(this.playhead);
    this.playhead += buf.duration;
    this.sources.add(src);
    src.onended = () => {
      this.sources.delete(src);
      if (this.sources.size === 0) {
        this.level = 0;
        this.onIdle?.();
      }
    };
  }

  interrupt() {
    for (const s of this.sources) {
      s.onended = null;
      s.stop();
    }
    this.sources.clear();
    this.level = 0;
    this.playhead = 0;
  }

  async close() {
    this.interrupt();
    await this.ctx?.close();
    this.ctx = null;
  }
}
